/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * Where a floating panel -- a menu, a filter's list -- is portalled to.
 *
 * Portalled so that no card with `overflow: hidden` can clip it, but not to
 * the end of `<body>`: everything a person can read has to sit inside a
 * landmark, and a panel hung off the body sat outside all of them, where
 * landmark navigation could not reach it (axe `region`, ADW-5553).
 *
 * The app frame mounts the layer (`FloatingLayer`) as the last thing in
 * `<main>`. It creates no stacking context and no containing block, so a
 * panel's `position: fixed` is still measured against the window and its
 * z-index still competes with the rail and the top bar, as it did from the
 * body.
 */
export const FLOATING_LAYER_ID = 'floating-layer';

/**
 * The element a floating panel is portalled into: the frame's layer, else the
 * page's own `<main>` (the sign-in screen renders outside the frame), and the
 * body only where there is no landmark at all -- a component rendered alone in
 * a test.
 *
 * A panel opened from inside a dialog stays on the body, after the dialog: a
 * dialog is a region of its own, and a panel in `<main>` would come before it
 * in the document and paint underneath it at the same z-index.
 */
export function floatingLayer(trigger?: Element | null): HTMLElement {
  if (trigger?.closest('[role="dialog"]')) return document.body;
  return (
    document.getElementById(FLOATING_LAYER_ID) ??
    document.querySelector<HTMLElement>('main') ??
    document.body
  );
}
